import React from 'react';
import { Input } from '@/components/ui/input';

/**
 * Custom mask component for CPF and CNPJ that works with React 19
 * Replaces react-input-mask which uses deprecated findDOMNode
 */

const formatCPF = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '').slice(0, 11);
  
  // Apply mask: XXX.XXX.XXX-XX
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

const formatCNPJ = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '').slice(0, 14);
  
  // Apply mask: XX.XXX.XXX/XXXX-XX
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

const formatPhone = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '').slice(0, 11);
  
  // Apply mask: (XX) XXXXX-XXXX or (XX) XXXX-XXXX
  if (digits.length <= 2) return digits.length > 0 ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

export const MaskedInput = React.forwardRef(({ 
  mask, 
  value, 
  onChange, 
  placeholder,
  ...props 
}, ref) => {
  const formatValue = (inputValue) => {
    switch (mask) {
      case 'cpf':
        return formatCPF(inputValue);
      case 'cnpj':
        return formatCNPJ(inputValue);
      case 'phone':
        return formatPhone(inputValue);
      default:
        return inputValue;
    }
  };

  const handleChange = (e) => {
    const rawValue = e.target.value;
    const formattedValue = formatValue(rawValue);
    
    // Create a synthetic event with the formatted value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: formattedValue
      }
    };
    
    onChange(syntheticEvent);
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    switch (mask) {
      case 'cpf':
        return '000.000.000-00';
      case 'cnpj':
        return '00.000.000/0000-00';
      case 'phone':
        return '(00) 00000-0000';
      default:
        return '';
    }
  };

  return (
    <Input
      ref={ref}
      value={value || ''}
      onChange={handleChange}
      placeholder={getPlaceholder()}
      {...props}
    />
  );
});

MaskedInput.displayName = 'MaskedInput';

export default MaskedInput;
